import { 
  FileText, 
  Video, 
  MicOff,
  Globe, 
  Users, 
  UserCheck,
  TrendingUp,
  MessageSquareMore,
  BarChart3,
  BrainCircuit,
  GraduationCap,
  MessageCircle,
  ClipboardCheck,
  Sparkles,
  Target
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Features = ({ audience = 'hiring' }) => {
  const { t } = useTranslation();
  const isTalent = audience === 'talent';

  const mainFeatures = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: t('features.feature1Title'),
      description: t('features.feature1Desc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: t('features.feature2Title'),
      description: t('features.feature2Desc'),
      color: 'text-accent-teal',
      bgColor: 'bg-accent-teal/10',
    },
    {
      icon: <MicOff className="w-8 h-8" />,
      title: t('features.feature3Title'),
      description: t('features.feature3Desc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: t('features.feature4Title'),
      description: t('features.feature4Desc'),
      color: 'text-accent-teal',
      bgColor: 'bg-accent-teal/10',
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: t('features.feature5Title'),
      description: t('features.feature5Desc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: <BrainCircuit className="w-8 h-8" />,
      title: t('features.feature6Title'),
      description: t('features.feature6Desc'),
      color: 'text-accent-teal',
      bgColor: 'bg-accent-teal/10',
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: t('features.feature7Title'),
      description: t('features.feature7Desc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('features.feature8Title'),
      description: t('features.feature8Desc'),
      color: 'text-accent-teal',
      bgColor: 'bg-accent-teal/10',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: t('features.feature9Title'),
      description: t('features.feature9Desc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: <UserCheck className="w-8 h-8" />,
      title: t('features.feature10Title'),
      description: t('features.feature10Desc'),
      color: 'text-accent-teal',
      bgColor: 'bg-accent-teal/10',
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: t('features.feature11Title'),
      description: t('features.feature11Desc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: <MessageSquareMore className="w-8 h-8" />,
      title: t('features.feature12Title'),
      description: t('features.feature12Desc'),
      color: 'text-accent-teal',
      bgColor: 'bg-accent-teal/10',
    },
  ];

  const talentProducts = [
    {
      id: 'cv-analysis',
      icon: <ClipboardCheck className="w-8 h-8" />,
      title: t('talentProducts.cvAnalysisTitle'),
      description: t('talentProducts.cvAnalysisDesc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      id: 'interview-prep',
      icon: <Sparkles className="w-8 h-8" />,
      title: t('talentProducts.interviewPrepTitle'),
      description: t('talentProducts.interviewPrepDesc'),
      color: 'text-accent-teal',
      bgColor: 'bg-accent-teal/10',
    },
    {
      id: 'role-growth',
      icon: <Target className="w-8 h-8" />,
      title: t('talentProducts.roleGrowthTitle'),
      description: t('talentProducts.roleGrowthDesc'),
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
  ];

  const featuresToRender = isTalent ? talentProducts : mainFeatures;

  return (
    <section id={isTalent ? 'talent-products' : 'features'} className="section-spacing bg-gray-50">
      <div className="container-custom px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-3 sm:mb-4">
            {isTalent ? t('talentProducts.title') : t('features.title')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            {isTalent ? t('talentProducts.subtitle') : t('features.subtitle')}
          </p>
        </div>

        {/* Features Grid */}
        <div className={`grid gap-4 sm:gap-6 lg:gap-8 ${isTalent ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
          {featuresToRender.map((feature, index) => (
            <div 
              key={feature.title}
              id={feature.id}
              className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1"
            >
              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl ${feature.bgColor} ${feature.color} mb-3 sm:mb-4`}>
                <div className="w-6 h-6 sm:w-8 sm:h-8">
                  {feature.icon}
                </div>
              </div>
              
              {/* Title */}
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
                {feature.title}
              </h3>
              
              {/* Description */}
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
